import React from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm, change } from 'redux-form';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import ReactTooltip from 'react-tooltip';
import { useMediaQuery } from 'react-responsive';

import Button from '../../Button';
import Paragraph from '../../Paragraph';
import Heading from '../../Heading';
import { Row, Col } from '../Scaffold';
import { renderField } from '../../Inputs';
import api from '../../../lib/api';

const validate = (valueSet) => {
  const values = valueSet;
  const errors = { client: {} };

  if (!values.client) values.client = {};
  // if (!values.client.title) errors.client.title = 'Please select your title';
  if (!values.client.first_name) errors.client.first_name = 'Please enter your first name';
  if (!values.client.last_name) errors.client.last_name = 'Please enter your last name';
  if (!values.client.gender) errors.client.gender = 'Please enter your gender';
  if (values.client.gender === 'Prefer to self describe as...' && !values.client.gender_other) errors.client.gender_other = 'Please enter your gender';
  if (!values.client.dob) errors.client.dob = 'Please enter a valid date of birth';
  if (!values.client.gp_practice_id) errors.client.gp_practice_id = 'Please enter your GP Practice';
  if (!values.client.ethnicity) errors.client.ethnicity = 'Please enter your ethnicity';
  if (!values.client.email) errors.client.email = 'Please enter your email address';
  if (!values.client.phone_mobile) errors.client.phone_mobile = 'Please enter your mobile phone number';
  if (!values.client.leave_message) errors.client.leave_message = 'Please select whether we can leave a message or not';
  if (!values.client.address1) errors.client.address1 = 'Please enter your address';
  if (!values.client.town) errors.client.town = 'Please enter your town';
  if (!values.client.postcode) errors.client.postcode = 'Please enter your postcode';
  if (!values.client.how_a) errors.client.how_a = 'Please select how you heard about us';
  if (!values.client.receive_marketing) errors.client.receive_marketing = 'Please select if you’d like to receive marketing emails or not';
  if (values.client.gender !== 'Male' && !values.client.activity_pregnant) errors.client.activity_pregnant = 'Please select if you are pregnant or not';
  if (!values.client.disability) errors.client.disability = 'Please select if you have any physical disabilities';
  if (!values.client.learning_disability) errors.client.learning_disability = 'Please select if you have any learning disabilities';
  if (!values.client.mental_health_user) errors.client.mental_health_user = 'Please select if you supported by the mental health services';

//   if (!values.client.password) {
//     errors.client.password = 'Please enter a password of at least 8 characters';
//   } else if (values.client.password.length < 8) {
//     errors.client.password = 'Please enter a password of at least 8 characters';
//   }

//   if (!values.client.confirm_password) {
//     errors.client.confirm_password = 'Please enter a confirmed password of at least 8 characters';
//   } else if (values.client.confirm_password.length < 8) {
//     errors.client.confirm_password = 'Please enter a confirmed password of at least 8 characters';
//   } else if (values.client.password !== values.client.confirm_password) {
//     errors.client.confirm_password = 'Please ensure passwords match';
//   }

  if (values.client.dob) {
    const now = moment();
    const age = moment.duration(now.diff(moment(values.client.dob)));
    if (age.asYears() < 18) {
      errors.client.dob = 'Sorry, you must be over 18 to register';
    }
  }

  const validPostcodes = ['BA', 'BH', 'DT', 'EX', 'PL', 'SP', 'TA', 'TQ'];
  if (values.client.postcode) {
    const shortPostcode = values.client.postcode.toUpperCase().substr(0, 2);
    if (validPostcodes.indexOf(shortPostcode) === -1) {
      errors.client.postcode = 'Sorry, we’re only available to clients who live in Dorset';
    }
  }

  if (!values.client.email) {
    errors.client.email = 'Please enter your email address';
  } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.client.email)) {
    errors.client.email = 'Please enter a valid email address';
  }

  if (values.client.how_a) {
    if (!values.client.how_b) errors.client.how_b = 'Please select where you heard about us';
    if (!values.client.how_c) errors.client.how_c = 'Please select what you heard about us';
  }

  if (values.client.disability === 'Yes' || values.client.learning_disability === 'Yes' || values.client.mental_health_user === 'Yes') {
    if (!values.client.disability_requirements) errors.client.disability_requirements = 'Please enter what support we can do for you';
  }
  // console.log(values.client);

  return errors;
};

function parseQuery(queryString) {
  const query = {};
  const pairs = (queryString[0] === '?' ? queryString.substr(1) : queryString).split('&');
  for (let i = 0; i < pairs.length; i += 1) {
    const pair = pairs[i].split('=');
    query[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || '');
  }
  return query;
}

const CustomLabel = (props) => {
  const {
    required, text, requiredSpec, tooltip, tooltipid,
  } = props;
  const isTablet = useMediaQuery({ query: '(max-width: 769px)' });
  const isMobile = useMediaQuery({ query: '(max-width: 540px)' });

  return ([
    <span key={0} className={required && 'required-star'}>{text}</span>,
    (requiredSpec && !isMobile) && <span key={1} className="required-spec-text">{requiredSpec}</span>,
    tooltip && [
      <span key={0} className="tooltip-trigger" data-tip data-for={tooltipid} data-event="click" ref={(ref) => { this[`${tooltipid}_trigger`] = ref; }}>
        {!isTablet && 'Why do we need this?'}
      </span>,
      <ReactTooltip
        key={1}
        id={tooltipid}
        ref={(el) => { this[tooltipid] = el; }}
        globalEventOff={isMobile ? '' : 'click'}
        className="tooltip-panel"
        textColor={isMobile ? '#000000' : '#FFFFFF'}
        arrowColor="#000000"
        backgroundColor="#000000"
        clickable
        effect="solid"
        overridePosition={({ left, top }, currentEvent, currentTarget, node) => {
          if (isMobile) {
            return { top: 0, left: 0 };
          }
          return { top, left };
        }}
        afterShow={() => {
          if (isMobile) {
            document.body.style.overflowY = 'hidden';
          }
        }}
        afterHide={() => {
          if (isMobile) {
            document.body.style.overflowY = 'auto';
          }
        }}
      >
        <i className="tooltip-close-btn" onClick={() => { this[tooltipid].tooltipRef = null; ReactTooltip.hide(this[`${tooltipid}_trigger`]); }} />
        <p>{tooltip}</p>
      </ReactTooltip>,
    ],
  ]);
};

class ClientInfo extends React.PureComponent {
  state = {
    referralId: this.props.how_a,
    dob: '',
    startDate: null,
    pregnant: '',
    gender: this.props.gender || null,
    showHDYHAU: true,
    disability: this.props.disability,
    learningDisability: this.props.learning_disability,
    mentalHealthUser: this.props.mental_health_user,
    how_a: '',
    how_b: '',
    how_c: '',
  };

  componentDidMount() {
    const qs = parseQuery(window.location.search);
    const { hash } = qs;

    if (hash) {
      api
        .proxyGet(`/hash/${hash}`)
        .then((response) => {
            console.log('data', response.data);
            if (response.data) {
            this.props.dispatch(change('registration', 'client[how_a]', response.data.how_a));
            this.props.dispatch(change('registration', 'client[how_b]', response.data.how_b));
            this.props.dispatch(change('registration', 'client[how_c]', response.data.how_c));
            this.setState({
                showHDYHAU: false,
                how_a: response.data.how_a,
                how_b: response.data.how_b,
                how_c: response.data.how_c
            });

            this.props.dispatch(change('registration', 'client[disability]', response.data.disability));
            this.props.dispatch(change('registration', 'client[learning_disability]', response.data.learning_disability));
            this.props.dispatch(change('registration', 'client[mental_health_user]', response.data.mental_health_user));
          }
        })
        .catch(error => console.error(error));
      console.log('hash', hash);
    }

    if (window.pageYOffset > this.sectionRef.offsetTop) {
      window.scrollTo({ top: this.sectionRef.offsetTop, behavior: 'smooth' });
    }
  }

  componentWillReceiveProps(props) {
    console.log('this is firing111', this.state.how_a, this.state.how_b, this.state.how_c);
    if (props.how_a && props.how_a !== this.props.how_a) {
        console.log('this is firing222', this.state.how_a, this.state.how_b, this.state.how_c);
      setTimeout(() => {
        this.props.dispatch(change('registration', 'client[how_b]', ''));
        this.props.dispatch(change('registration', 'client[how_c]', ''));
      }, 10);
    } else {
        console.log('trigger client update');
        if(this.state.how_a) this.props.dispatch(change('registration', 'client[how_a]', this.state.how_a));
        if(this.state.how_b) this.props.dispatch(change('registration', 'client[how_b]', this.state.how_b));
        if(this.state.how_c) this.props.dispatch(change('registration', 'client[how_c]', this.state.how_c));
    }
  }

  render() {
    const { handleSubmit, previousPage } = this.props;

    let HDYHAU = [
      <Field
        name="client[how_a]"
        type="select"
        component={renderField}
        onChange={(e, referralId) => this.setState({ referralId })}
        params={{ dataKey: 'referral', searchable: true }}
        label={<CustomLabel text="How did you hear about us?" required />}
        key={1}
      />];

    if (this.state.referralId) {
      HDYHAU.push(<div className="inputs-group" key={2}>
        <Field
          name="client[how_b]"
          type="select"
          component={renderField}
          params={{
            dataKey: 'referrals', level: 2, referralId: this.state.referralId, searchable: true,
          }}
          label={<CustomLabel text="Where from?" required />}
        />
        <Field
          name="client[how_c]"
          type="select"
          component={renderField}
          params={{
            dataKey: 'referrals', level: 3, referralId: this.state.referralId, searchable: true,
          }}
          label={<CustomLabel text="What was it in regard to?" required />}
        />
      </div>);
    }

    if (!this.state.showHDYHAU) {
      HDYHAU = [];
    }

    return (
        <div
            className='client-info'
            ref={(sectionRef) => {
                this.sectionRef = sectionRef;
            }}>
            <div className='justifycentre register-block client-header'>
                <Heading tag='h1' classes='header-text smaller'>
                    <span id='register3'>Your details</span>
                </Heading>
                <Paragraph classes='larger'>Tell us about yourself so we have a better understanding of who you are and how we can help.</Paragraph>
            </div>
            <form onSubmit={handleSubmit} className='form-register'>
                <div className='width-limited smaller form-register-section'>
                    <h3 className='header-text smallest'>About you</h3>

                    {/* <Field name="client[title]" type="select" params={{ dataKey: 'title' }} component={renderField} label="Your title *" /> */}
                    <Field name='client[first_name]' type='text' component={renderField} label={<CustomLabel text='First name' required />} classes='text-input-field' />
                    <Field name='client[last_name]' type='text' component={renderField} label={<CustomLabel text='Last name' required />} classes='text-input-field' />
                    <Field name='client[dob]' type='date' component={renderField} label={<CustomLabel text='Date of birth' required />} />
                    <Field name='client[gp_practice_id]' type='select' params={{ dataKey: 'gpSurgery', searchable: true }} component={renderField} label={<CustomLabel text='GP practice' required tooltipid='gp_practice_tooltip' tooltip='We will not share your information with your GP. We ask this so we can confirm if you’re eligible to receive our support.' />} />
                    <Field name='client[gender]' type='select' params={{ dataKey: 'gender' }} component={renderField} onChange={(e, gender) => this.setState({ gender })} label={<CustomLabel text='Gender' required tooltipid='gender_tooltip' tooltip='Gender has implications for our health. Some genders are more prone to particular health risks. Knowing your gender helps us match your results to recommended guidelines.' />} />
                    {this.state.gender === 'Prefer to self describe as...' ? <Field name='client[gender_other]' type='text' component={renderField} label={<CustomLabel text='Your gender' required />} classes='text-input-field' /> : null}
                    <Field name='client[ethnicity]' type='select' params={{ dataKey: 'ethnicity' }} component={renderField} label={<CustomLabel text='Ethnicity' required tooltipid='ethnicity_tooltip' tooltip='Some ethnicities are more prone to particular health risks. Knowing your ethnicity helps us match your results to recommended guidelines.' />} />
                    <Field name='client[phone_mobile]' type='text' component={renderField} label={<CustomLabel text='Mobile number' required />} classes='text-input-field' />
                    <Field name='client[leave_message]' type='radiogroup' params={{ dataKey: 'LeaveMessage', classes: 'black reversed' }} component={renderField} label={<CustomLabel text="Are you happy for us to leave a message if you're not available?" required />} centered />

                    <Field name='client[address1]' type='text' component={renderField} label={<CustomLabel text='Address line 1' required />} classes='text-input-field' />
                    <Field name='client[address2]' type='text' component={renderField} label={<CustomLabel text='Address line 2' />} classes='text-input-field' />
                    <Field name='client[town]' type='text' component={renderField} label={<CustomLabel text='Town / City' required />} classes='text-input-field' />
                    <Field name='client[postcode]' type='text' component={renderField} label={<CustomLabel text='Postcode' required />} classes='text-input-field postcode-input-field' />
                </div>

                <div className='width-limited smaller form-register-section'>
                    <h3 className='header-text smallest'>Your health and wellbeing</h3>

                    {this.state.gender !== 'Male' && <Field name='client[activity_pregnant]' type='radiogroup' params={{ dataKey: 'LeaveMessage', classes: 'black reversed' }} component={renderField} label={<CustomLabel text='Are you currently pregnant?' required />} onChange={(ks, pregnant) => this.setState({ pregnant })} centered />}

                    <Field name='client[disability]' type='radiogroup' params={{ dataKey: 'LeaveMessage', classes: 'black reversed' }} component={renderField} label={<CustomLabel text='Do you have any physical disabilities?' required tooltipid='disability_tooltip' tooltip="We'll take this into consideration in any support we provide you with." />} onChange={(e, disability) => this.setState({ disability })} centered />
                    <Field name='client[learning_disability]' type='radiogroup' params={{ dataKey: 'LeaveMessage', classes: 'black reversed' }} component={renderField} label={<CustomLabel text='Do you have any learning disabilities?' required tooltipid='learning_disability_tooltip' tooltip="We'll take this into consideration in any support we provide you with." />} onChange={(e, learningDisability) => this.setState({ learningDisability })} centered />
                    <Field name='client[mental_health_user]' type='radiogroup' params={{ dataKey: 'LeaveMessage', classes: 'black reversed' }} component={renderField} label={<CustomLabel text='Are you supported by the mental health services?' required tooltipid='mental_health_tooltip' tooltip="We'll take this into consideration in any support we provide you with." />} onChange={(e, mentalHealthUser) => this.setState({ mentalHealthUser })} centered />
                    {(this.state.disability === 'Yes' || this.state.learningDisability === 'Yes' || this.state.mentalHealthUser === 'Yes') && <Field name='client[disability_requirements]' type='text' component={renderField} label={<CustomLabel text='Is there anything we can consider to support your needs' required />} classes='text-input-field' />}

                    <Field name='client[other_info]' type='textarea' component={renderField} label={<CustomLabel text='Anything else we should know?' />} classes='text-input-field textarea' />
                </div>

                <div className='width-limited smaller form-register-section'>
                    <h3 className='header-text smallest'>Account details</h3>

                    <Field name='client[email]' type='email' component={renderField} label={<CustomLabel text='Email address' required />} classes='text-input-field' />
                    {/* <Field name="client[password]" type="password" component={renderField} label={<CustomLabel text="Create a password" requiredSpec="This needs to be more than 8 characters." required />} classes="text-input-field" />
                    <Field name="client[confirm_password]" type="password" component={renderField} label={<CustomLabel text="Confirm your password" required />} classes="text-input-field" /> */}

                    {HDYHAU}

                    <Field name='client[receive_marketing]' type='radiogroup' params={{ dataKey: 'contact', classes: 'black reversed' }} component={renderField} label={<CustomLabel text='Would you like to receive our monthly news and stories email?' required />} centered />
                    <Paragraph classes='marketing-notice'>
                        <span>You can unsubscribe at any time.</span>
                    </Paragraph>

                    <Paragraph classes='button-groups flex-row divideline'>
                        <Button classes={['color-button']} label='Next' onClick={handleSubmit} />
                        <Button classes={['underline-button']} label='Back' onClick={previousPage} />
                    </Paragraph>
                </div>
            </form>
        </div>
    );
  }
}

ClientInfo.propTypes = {
  previousPage: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  how_a: PropTypes.string.isRequired,
  pregnant: PropTypes.string.isRequired,
};

export default reduxForm({
  form: 'registration', // <------ same form name
  destroyOnUnmount: false, // <------ preserve form data
  forceUnregisterOnUnmount: true, // <------ unregister fields on unmount
  validate,
  onSubmitFail: (errors) => {
    const errorEl = document.querySelector(Object.keys(errors.client).map(
      fieldName => fieldName === 'dob' ? `[name="day"]` : `[name="client[${fieldName}]"]`
    ).join(','));

    if (typeof errorEl.getAttribute !== 'undefined' && errorEl.getAttribute('type') === 'hidden') {
      const nextEl = errorEl.nextElementSibling;
      if (nextEl.classList.contains('select-search-box-search')) {
        nextEl.focus();
      }
    } else {
      if (errorEl && errorEl.focus) {
        errorEl.focus();
      }
    }
  }
})(ClientInfo);



// WEBPACK FOOTER //
// ./src/Components/Register/FormPages/ClientInfo.jsx